import React, { useState, useEffect } from 'react';
import { X, Download, AlertCircle, Loader } from 'lucide-react';
import { reportsAPI } from '../../utils/api';

export default function ReportViewerModal({ jobId, onClose }) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadReport();
  }, [jobId]);

  const loadReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await reportsAPI.fetchPDF(jobId);
      
      // Basic sanity check: if the blob is tiny and content-type isn't pdf, it might be a JSON error
      if (res.data.type === 'application/json' || res.data.size < 500) {
        const text = await res.data.text();
        try {
          const json = JSON.parse(text);
          if (json.error) throw new Error(json.error);
        } catch (e) {
          if (text.includes('error')) throw new Error('Backend failed to generate report');
        }
      }

      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPdfUrl(url);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load report generated from backend.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!pdfUrl) return;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `FieldOps-Report-${jobId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '900px', width: '95%', height: '90vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ padding: '16px 24px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>Report Preview</h2>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button className="btn-secondary" onClick={handleDownload} disabled={!pdfUrl} style={{ gap: '6px' }}>
              <Download size={16} /> Download
            </button>
            <button className="btn-icon" onClick={onClose}><X size={20} /></button>
          </div>
        </div>

        <div className="modal-body" style={{ flex: 1, padding: 0, overflow: 'hidden', backgroundColor: '#e5e7eb', position: 'relative' }}>
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '12px' }}>
              <Loader className="spin" size={32} color="var(--primary)" />
              <div style={{ color: 'var(--text-secondary)' }}>Generating premium report...</div>
            </div>
          ) : error ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--danger)', gap: '12px' }}>
              <AlertCircle size={48} />
              <div style={{ fontWeight: 500 }}>{error}</div>
              <button className="btn-primary" onClick={loadReport} style={{ marginTop: '12px' }}>Retry</button>
            </div>
          ) : (
            <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
              <iframe 
                src={pdfUrl} 
                title="Report Preview" 
                style={{ width: '100%', height: '100%', border: 'none', flex: 1 }} 
              />
              <div className="hide-desktop" style={{ padding: '12px', background: '#fff', borderTop: '1px solid var(--border)', textAlign: 'center' }}>
                 <button className="btn btn-primary" onClick={handleDownload} style={{ width: '100%' }}>
                   <Download size={16} /> Save PDF to Device
                 </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
